import { ArrowUpRight, Calendar, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import type { Event, HeroConfig } from "@/lib/event-schema";
import { Ornament } from "@/components/ui/Ornament";
import { cn } from "@/lib/cn";

type Props = {
  config: HeroConfig;
  event: Event;
};

export function HeroModule({ config, event }: Props) {
  const { eyebrow, title, subtitle, date, location, banner, ctas } = config;

  // Filter CTAs whose path references a module that is disabled on the
  // current event. We check by the module segment alone — a button
  // labeled "Sign up for potluck" should disappear when Potluck is
  // disabled, even if a stale cloned CTA still carries a different
  // event's slug. External links (http/https) and links without a
  // module segment pass through untouched.
  const visibleCtas = (ctas ?? []).filter((cta) => {
    if (/^https?:\/\//i.test(cta.to)) return true;
    const match = cta.to.match(/^\/[^/?#]+\/([^/?#]+)/);
    if (!match) return true;
    const ctaModule = match[1] as keyof typeof event.modules;
    if (!(ctaModule in event.modules)) return true;
    const moduleConfig = event.modules[ctaModule];
    return Boolean(moduleConfig?.enabled);
  });

  return (
    <section
      className="relative isolate overflow-hidden"
      aria-labelledby={`${event.slug}-hero-title`}
    >
      {/* Decorative geometric backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.18] dark:opacity-[0.22]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgb(var(--accent) / 0.45), transparent 35%), radial-gradient(circle at 80% 20%, rgb(var(--accent) / 0.20), transparent 40%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[420px] bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 200 200%22><g fill=%22none%22 stroke=%22%23B7791F%22 stroke-width=%220.6%22 opacity=%220.18%22><circle cx=%22100%22 cy=%22100%22 r=%2280%22/><circle cx=%22100%22 cy=%22100%22 r=%2260%22/><circle cx=%22100%22 cy=%22100%22 r=%2240%22/><path d=%22M20 100h160M100 20v160M30 30l140 140M30 170L170 30%22/></g></svg>')] bg-center bg-no-repeat opacity-30"
      />

      <div className="mx-auto grid w-full max-w-6xl gap-12 px-5 pb-16 pt-12 sm:px-6 lg:grid-cols-[1.05fr,0.95fr] lg:gap-16 lg:px-8 lg:pb-24 lg:pt-20">
        {/* LEFT — title block */}
        <div className="flex flex-col justify-center animate-rise">
          {eyebrow ? (
            <p className="eyebrow mb-5 flex items-center gap-2.5">
              <span className="inline-block h-1 w-1 rounded-full bg-accent" />
              {eyebrow}
            </p>
          ) : null}

          <h1
            id={`${event.slug}-hero-title`}
            className={cn(
              "display-title text-balance text-[clamp(2.5rem,7.2vw,5.25rem)] leading-[0.98]",
              "[&_em]:font-light [&_em]:not-italic [&_em]:text-accent",
            )}
          >
            {renderHeroTitle(title)}
          </h1>

          {subtitle ? (
            <p className="mt-6 max-w-xl text-pretty text-base text-muted sm:text-lg leading-relaxed">
              {subtitle}
            </p>
          ) : null}

          <dl className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-accent" strokeWidth={1.5} />
              <dt className="sr-only">Date</dt>
              <dd className="font-medium text-ink">{date}</dd>
            </div>
            {location ? (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-accent" strokeWidth={1.5} />
                <dt className="sr-only">Location</dt>
                <dd className="text-muted">{location}</dd>
              </div>
            ) : null}
          </dl>

          {visibleCtas.length ? (
            <div className="mt-10 flex flex-wrap gap-3">
              {visibleCtas.map((cta, i) => (
                <Link
                  key={cta.to}
                  to={cta.to}
                  className={cn(
                    "group inline-flex items-center gap-2 rounded-full px-5 py-3 text-[15px] font-medium transition-all duration-200 focus-ring",
                    cta.variant === "outline"
                      ? "border border-line/80 text-ink hover:border-accent hover:text-accent"
                      : cta.variant === "subtle"
                        ? "bg-accent-soft text-accent-ink border border-accent/20 hover:bg-accent/20"
                        : "bg-ink text-canvas hover:bg-ink/90 dark:bg-accent dark:text-accent-on shadow-glow",
                  )}
                  style={{ animationDelay: `${120 * (i + 1)}ms` }}
                >
                  {cta.label}
                  <ArrowUpRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    strokeWidth={1.75}
                  />
                </Link>
              ))}
            </div>
          ) : null}
        </div>

        {/* RIGHT — banner card */}
        <div className="relative">
          <HeroVisual banner={banner} />
        </div>
      </div>

      <div className="px-5 sm:px-6 lg:px-8">
        <Ornament className="mx-auto max-w-xl" />
      </div>
    </section>
  );
}

function renderHeroTitle(title: string) {
  // Allow simple markup: wrap *text* in <em> for accent.
  const parts = title.split(/(\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("*") && part.endsWith("*")) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return <span key={i}>{part}</span>;
  });
}

function HeroVisual({ banner }: { banner?: HeroConfig["banner"] }) {
  if (banner?.src) {
    const srcSet = banner.variants
      ?.filter((v) => v.format === "webp" || v.format === "jpeg")
      .map((v) => `${v.url} ${v.width}w`)
      .join(", ");

    return (
      <div className="group relative aspect-[4/5] w-full max-w-[520px] justify-self-end overflow-hidden rounded-3xl border border-line/60 bg-surface shadow-card">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-tr from-canvas/30 via-transparent to-transparent dark:from-black/40"
        />
        <img
          src={banner.src}
          srcSet={srcSet}
          sizes="(min-width: 1024px) 520px, 100vw"
          alt={banner.alt}
          className="h-full w-full object-cover"
          loading="eager"
        />
        {banner.alt ? <AltPeek alt={banner.alt} /> : null}
      </div>
    );
  }
  return <HeroPlaceholder />;
}

function AltPeek({ alt }: { alt: string }) {
  // Small "i" pointer in the corner that expands on hover/focus to reveal
  // the alt text. Keyboard accessible (focusable via tabIndex), and the
  // alt text is also exposed via the <img alt> attribute for assistive
  // tech, so this overlay is purely sighted-user convenience.
  return (
    <div className="absolute right-3 top-3 z-20">
      <button
        type="button"
        aria-label={`Show image description: ${alt}`}
        className="peer grid h-7 w-7 place-items-center rounded-full border border-white/40 bg-black/35 text-[11px] font-medium text-white/90 backdrop-blur transition-colors hover:bg-black/55 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
      >
        i
      </button>
      <div
        className="pointer-events-none absolute right-0 top-9 max-w-[260px] origin-top-right rounded-xl border border-white/30 bg-black/55 px-3 py-2 text-xs text-white/95 backdrop-blur-md opacity-0 scale-95 transition-all duration-150 peer-hover:pointer-events-auto peer-hover:opacity-100 peer-hover:scale-100 peer-focus-visible:pointer-events-auto peer-focus-visible:opacity-100 peer-focus-visible:scale-100"
        role="tooltip"
      >
        {alt}
      </div>
    </div>
  );
}

function HeroPlaceholder() {
  return (
    <div className="relative aspect-[4/5] w-full max-w-[520px] justify-self-end overflow-hidden rounded-3xl border border-line/60 bg-paper shadow-card">
      <svg
        viewBox="0 0 400 500"
        className="absolute inset-0 h-full w-full"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden
      >
        <defs>
          <radialGradient id="ph" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="rgb(var(--accent))" stopOpacity="0.45" />
            <stop offset="60%" stopColor="rgb(var(--accent))" stopOpacity="0.08" />
            <stop offset="100%" stopColor="rgb(var(--accent))" stopOpacity="0" />
          </radialGradient>
          <pattern
            id="grid"
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(8)"
          >
            <path
              d="M 40 0 L 0 0 0 40"
              fill="none"
              stroke="rgb(var(--accent))"
              strokeOpacity="0.18"
              strokeWidth="0.5"
            />
          </pattern>
        </defs>
        <rect width="400" height="500" fill="url(#grid)" />
        <circle cx="200" cy="200" r="160" fill="url(#ph)" />
        <g
          stroke="rgb(var(--accent))"
          strokeOpacity="0.5"
          fill="none"
          strokeWidth="0.75"
        >
          <circle cx="200" cy="200" r="120" />
          <circle cx="200" cy="200" r="80" />
          <g transform="translate(200 200)">
            {Array.from({ length: 12 }).map((_, i) => (
              <line
                key={i}
                x1="0"
                y1="-120"
                x2="0"
                y2="-80"
                transform={`rotate(${i * 30})`}
              />
            ))}
          </g>
        </g>
      </svg>
      <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-line/60 bg-surface-raised/80 px-4 py-3 backdrop-blur">
        <p className="eyebrow text-accent">Banner pending</p>
        <p className="mt-1 text-xs text-muted">
          The organizer hasn't uploaded an image yet.
        </p>
      </div>
    </div>
  );
}
